import type { Transaction } from '@/types';

export interface FilterCriteria {
  searchQuery?: string;
  type?: 'all' | 'gain' | 'expense';
  categoryId?: string | null;
  dateRange?: {
    start: Date;
    end: Date;
  } | null;
}

/**
 * Filter transactions based on multiple criteria
 */
export function filterTransactions(
  transactions: Transaction[],
  criteria: FilterCriteria
): Transaction[] {
  let filtered = [...transactions];

  // Filter by search query (name)
  if (criteria.searchQuery && criteria.searchQuery.trim()) {
    const query = criteria.searchQuery.toLowerCase().trim();
    filtered = filtered.filter((t) =>
      t.name.toLowerCase().includes(query) ||
      (t.notes && t.notes.toLowerCase().includes(query))
    );
  }

  // Filter by type
  if (criteria.type && criteria.type !== 'all') {
    filtered = filtered.filter((t) => t.type === criteria.type);
  }

  // Filter by category
  if (criteria.categoryId) {
    filtered = filtered.filter((t) => t.categoryId === criteria.categoryId);
  }

  // Filter by date range
  if (criteria.dateRange) {
    const { start, end } = criteria.dateRange;
    filtered = filtered.filter((t) => {
      const transactionDate = t.date instanceof Date ? t.date : new Date(t.date);
      return transactionDate >= start && transactionDate <= end;
    });
  }

  return filtered;
}

/**
 * Get filter summary statistics
 */
export function getFilterStats(transactions: Transaction[]) {
  const totalIncome = transactions
    .filter((t) => t.type === 'gain')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalExpenses = transactions
    .filter((t) => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);

  const netSavings = totalIncome - totalExpenses;

  return {
    count: transactions.length,
    totalIncome,
    totalExpenses,
    netSavings,
    incomeCount: transactions.filter((t) => t.type === 'gain').length,
    expenseCount: transactions.filter((t) => t.type === 'expense').length,
  };
}